import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { PlayerModel } from '../../utils/majorAnalysis';
import { seriesColor } from '../../utils/chartPalette';

// ── Sorting ──────────────────────────────────────────────────────────────────
// Every table opens in tournament-rank order. Clicking a header sorts by it
// (again to flip); clicking Rank goes back to the tournament order. Rows with
// no value always sink to the bottom.

export interface SortState { key: string; dir: 1 | -1 }
export const RANK_SORT: SortState = { key: 'rank', dir: 1 };

export function useSort(initial: SortState = RANK_SORT) {
  const [sort, setSort] = useState<SortState>(initial);
  const toggle = (key: string, firstDir: 1 | -1 = 1) =>
    setSort((s) => (key === 'rank' ? RANK_SORT : s.key === key ? { key, dir: (-s.dir) as 1 | -1 } : { key, dir: firstDir }));
  return { sort, toggle, reset: () => setSort(RANK_SORT) };
}

export function sortRows<T>(
  rows: T[], sort: SortState, orderOf: (row: T) => number, valueOf: (row: T, key: string) => number | string | null | undefined,
): T[] {
  if (sort.key === 'rank') return [...rows].sort((a, b) => (orderOf(a) - orderOf(b)) * sort.dir);
  return [...rows].sort((a, b) => {
    const va = valueOf(a, sort.key);
    const vb = valueOf(b, sort.key);
    const na = va == null || (typeof va === 'number' && !Number.isFinite(va));
    const nb = vb == null || (typeof vb === 'number' && !Number.isFinite(vb));
    if (na || nb) return na === nb ? orderOf(a) - orderOf(b) : na ? 1 : -1;
    const c = typeof va === 'string' ? va.localeCompare(String(vb)) : (va as number) - (vb as number);
    return c * sort.dir || orderOf(a) - orderOf(b);
  });
}

export const SortTh: React.FC<{
  label: React.ReactNode;
  sortKey: string;
  sort: SortState;
  onSort: (key: string, firstDir?: 1 | -1) => void;
  /** Direction of the first click: -1 when bigger is better. */
  firstDir?: 1 | -1;
  title?: string;
  className?: string;
}> = ({ label, sortKey, sort, onSort, firstDir = 1, title, className }) => {
  const active = sort.key === sortKey;
  return (
    <th className={className} aria-sort={active ? (sort.dir === 1 ? 'ascending' : 'descending') : 'none'} title={title}>
      <button type="button" className={`ma-sort${active ? ' is-active' : ''}`} onClick={() => onSort(sortKey, firstDir)}>
        {label}
        <span className="ma-sort-ind" aria-hidden>{active ? (sort.dir === 1 ? '▲' : '▼') : '↕'}</span>
      </button>
    </th>
  );
};

// ── Player cells ─────────────────────────────────────────────────────────────

export function rankText(p: PlayerModel, all: PlayerModel[]): string {
  if (p.status === 'dq') return 'DQ';
  if (p.status === 'dnf') return 'DNF';
  if (p.status === 'guest') return 'G';
  if (p.rank == null) return '—';
  const tied = all.filter((o) => o.status === 'ranked' && o.rank === p.rank).length > 1;
  return `${tied ? 'T' : ''}${p.rank}`;
}

export const RankCell: React.FC<{ p: PlayerModel; all: PlayerModel[] }> = ({ p, all }) => {
  const text = rankText(p, all);
  const tone = p.status !== 'ranked' ? 'muted' : p.rank === 1 ? 'gold' : p.rank === 2 ? 'silver' : p.rank === 3 ? 'bronze' : 'plain';
  const title = p.status === 'dq' ? 'Disqualified' : p.status === 'dnf' ? "Did not finish every round" : p.status === 'guest' ? 'Guest — not in contention' : `Finished ${text}`;
  return <td className="c ma-rank-td ma-stick-1"><span className={`ma-rank ma-rank--${tone}`} title={title}>{text}</span></td>;
};

export const PlayerCell: React.FC<{ p: PlayerModel }> = ({ p }) => (
  <td className="ma-name-td ma-stick-2">
    <span className="ma-swatch" style={{ background: seriesColor(p.order) }} aria-hidden />
    <span className="ma-name-full">{p.name}</span>
    <span className="ma-name-short">{p.first_name} {p.last_name.charAt(0)}.</span>
  </td>
);

// ── Controls ─────────────────────────────────────────────────────────────────

export function Seg<T extends string | number>({ value, options, onChange, label, disabled, title }: {
  value: T;
  options: { value: T; label: React.ReactNode; title?: string }[];
  onChange: (v: T) => void;
  label: string;
  disabled?: boolean;
  title?: string;
}) {
  return (
    <div className={`ma-seg${disabled ? ' is-disabled' : ''}`} role="group" aria-label={label} title={title}>
      {options.map((o) => (
        <button
          key={String(o.value)}
          type="button"
          className={o.value === value ? 'active' : ''}
          aria-pressed={o.value === value}
          disabled={disabled}
          title={o.title}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

// ── Numbers ──────────────────────────────────────────────────────────────────

export const fmt1 = (v: number | null | undefined) => (v == null ? '—' : (Math.round(v * 10) / 10).toFixed(1));
export const fmtInt = (v: number | null | undefined) => (v == null ? '—' : String(Math.round(v)));
export const fmtPct = (num: number, den: number) => (den ? `${Math.round((num / den) * 100)}%` : '—');
/** A score that may carry a fraction of a handicap: whole numbers stay whole. */
export const fmtScore = (v: number | null | undefined) => (v == null ? '—' : Number.isInteger(Math.round(v * 10) / 10) ? String(Math.round(v)) : fmt1(v));

// ── Layout ───────────────────────────────────────────────────────────────────

/** Width of an element, kept current — charts draw at real pixels so text never shrinks. */
export function useElementWidth<T extends HTMLElement>(fallback = 720) {
  const ref = useRef<T | null>(null);
  const [width, setWidth] = useState(fallback);
  useLayoutEffect(() => {
    if (ref.current?.clientWidth) setWidth(ref.current.clientWidth);
  }, []);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w) setWidth(Math.round(w));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width] as const;
}

export const Empty: React.FC<{ title: string; children?: React.ReactNode }> = ({ title, children }) => (
  <div className="ma-empty">
    <div className="ma-empty-title">{title}</div>
    {children}
  </div>
);
